import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Search, Filter, CheckCircle, Clock, XCircle, CircleSlash, X, Plus, Target, Building2, Monitor, Newspaper, Maximize2 } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useState } from "react";
import { Badge } from "@/components/ui/badge";

type FilterStatus = 'all' | 'active' | 'scheduled' | 'expired' | 'inactive';
type TargetFilter = 'all' | 'targeted' | 'global';
type DisplayTypeFilter = 'all' | 'banner' | 'news' | 'modal';

interface BannerFiltersProps {
  onSearchChange: (value: string) => void;
  onStatusChange: (value: FilterStatus) => void;
  onTargetChange: (value: TargetFilter) => void;
  onDisplayTypeChange?: (value: DisplayTypeFilter) => void;
  statusValue: FilterStatus;
  targetValue: TargetFilter;
  displayTypeValue?: DisplayTypeFilter;
  onAddBanner?: () => void;
}

export function BannerFilters({
  onSearchChange,
  onStatusChange,
  onTargetChange,
  onDisplayTypeChange,
  statusValue,
  targetValue,
  displayTypeValue = 'all',
  onAddBanner,
}: BannerFiltersProps) {
  // Dropdown açık/kapalı durumu
  const [open, setOpen] = useState(false);

  // Filtreleri sıfırla ve menüyü kapat
  const resetFilters = () => {
    onStatusChange('all');
    onTargetChange('all');
    onDisplayTypeChange?.('all');
    setOpen(false);
  };

  // Herhangi bir filtre aktif mi?
  const isAnyFilterActive = statusValue !== 'all' || targetValue !== 'all' || displayTypeValue !== 'all';

  // Aktif filtre sayısı
  const activeFilterCount = [statusValue !== 'all', targetValue !== 'all', displayTypeValue !== 'all'].filter(Boolean).length;

  // Tıklama işleyicileri
  const handleStatusChange = (value: FilterStatus) => {
    onStatusChange(value === statusValue ? 'all' : value);
    setOpen(false);
  };

  const handleTargetChange = (value: TargetFilter) => {
    onTargetChange(value === targetValue ? 'all' : value);
    setOpen(false);
  };

  const handleDisplayTypeChange = (value: DisplayTypeFilter) => {
    onDisplayTypeChange?.(value === displayTypeValue ? 'all' : value);
    setOpen(false);
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
      <div className="space-y-4">
        {/* Arama ve Filtre */}
        <div className="flex gap-3 items-center">
          <div className="relative flex-1">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
            <Input
              placeholder="Duyuru başlığı veya içeriği ile arayın..."
              onChange={(e) => onSearchChange(e.target.value)}
              className="pl-12 h-11 border-slate-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
            />
          </div>

          <DropdownMenu open={open} onOpenChange={setOpen}>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                className={`h-11 px-5 rounded-lg border-slate-200 hover:bg-slate-50 ${isAnyFilterActive ? "bg-purple-50 border-purple-300 text-purple-700 hover:bg-purple-100" : ""}`}
              >
                <Filter className="mr-2 h-4 w-4" />
                Filtrele
                {isAnyFilterActive && (
                  <span className="ml-2 bg-purple-600 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                    {activeFilterCount}
                  </span>
                )}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56 p-2">
              {/* Tip Filtreleri */}
              <div className="px-2 py-1.5 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                Duyuru Tipi
              </div>
              <DropdownMenuItem
                onClick={() => handleDisplayTypeChange('banner')}
                className={`cursor-pointer py-2.5 px-3 rounded-md ${displayTypeValue === 'banner' ? 'bg-purple-50' : ''}`}
              >
                <div className="flex items-center w-full">
                  <Monitor className={`mr-3 h-4 w-4 ${displayTypeValue === 'banner' ? 'text-purple-600' : 'text-slate-400'}`} />
                  <span className={displayTypeValue === 'banner' ? 'font-medium text-purple-700' : 'text-slate-700'}>Banner</span>
                </div>
              </DropdownMenuItem>

              <DropdownMenuItem
                onClick={() => handleDisplayTypeChange('news')}
                className={`cursor-pointer py-2.5 px-3 rounded-md ${displayTypeValue === 'news' ? 'bg-blue-50' : ''}`}
              >
                <div className="flex items-center w-full">
                  <Newspaper className={`mr-3 h-4 w-4 ${displayTypeValue === 'news' ? 'text-blue-600' : 'text-slate-400'}`} />
                  <span className={displayTypeValue === 'news' ? 'font-medium text-blue-700' : 'text-slate-700'}>Haber</span>
                </div>
              </DropdownMenuItem>

              <DropdownMenuItem
                onClick={() => handleDisplayTypeChange('modal')}
                className={`cursor-pointer py-2.5 px-3 rounded-md ${displayTypeValue === 'modal' ? 'bg-orange-50' : ''}`}
              >
                <div className="flex items-center w-full">
                  <Maximize2 className={`mr-3 h-4 w-4 ${displayTypeValue === 'modal' ? 'text-orange-600' : 'text-slate-400'}`} />
                  <span className={displayTypeValue === 'modal' ? 'font-medium text-orange-700' : 'text-slate-700'}>Modal (Popup)</span>
                </div>
              </DropdownMenuItem>

              <DropdownMenuSeparator className="my-2" />

              {/* Durum Filtreleri */}
              <div className="px-2 py-1.5 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                Duyuru Durumu
              </div>
              <DropdownMenuItem
                onClick={() => handleStatusChange('active')}
                className={`cursor-pointer py-2.5 px-3 rounded-md ${statusValue === 'active' ? 'bg-green-50' : ''}`}
              >
                <div className="flex items-center w-full">
                  <CheckCircle className={`mr-3 h-4 w-4 ${statusValue === 'active' ? 'text-green-600' : 'text-slate-400'}`} />
                  <span className={statusValue === 'active' ? 'font-medium text-green-700' : 'text-slate-700'}>Aktif</span>
                </div>
              </DropdownMenuItem>

              <DropdownMenuItem
                onClick={() => handleStatusChange('scheduled')}
                className={`cursor-pointer py-2.5 px-3 rounded-md ${statusValue === 'scheduled' ? 'bg-blue-50' : ''}`}
              >
                <div className="flex items-center w-full">
                  <Clock className={`mr-3 h-4 w-4 ${statusValue === 'scheduled' ? 'text-blue-600' : 'text-slate-400'}`} />
                  <span className={statusValue === 'scheduled' ? 'font-medium text-blue-700' : 'text-slate-700'}>Planlanmış</span>
                </div>
              </DropdownMenuItem>

              <DropdownMenuItem
                onClick={() => handleStatusChange('expired')}
                className={`cursor-pointer py-2.5 px-3 rounded-md ${statusValue === 'expired' ? 'bg-red-50' : ''}`}
              >
                <div className="flex items-center w-full">
                  <XCircle className={`mr-3 h-4 w-4 ${statusValue === 'expired' ? 'text-red-600' : 'text-slate-400'}`} />
                  <span className={statusValue === 'expired' ? 'font-medium text-red-700' : 'text-slate-700'}>Süresi Dolmuş</span>
                </div>
              </DropdownMenuItem>

              <DropdownMenuItem
                onClick={() => handleStatusChange('inactive')}
                className={`cursor-pointer py-2.5 px-3 rounded-md ${statusValue === 'inactive' ? 'bg-gray-50' : ''}`}
              >
                <div className="flex items-center w-full">
                  <CircleSlash className={`mr-3 h-4 w-4 ${statusValue === 'inactive' ? 'text-gray-600' : 'text-slate-400'}`} />
                  <span className={statusValue === 'inactive' ? 'font-medium text-gray-700' : 'text-slate-700'}>Pasif</span>
                </div>
              </DropdownMenuItem>

              <DropdownMenuSeparator className="my-2" />

              {/* Hedefleme Filtreleri */}
              <div className="px-2 py-1.5 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                Hedefleme
              </div>
              <DropdownMenuItem
                onClick={() => handleTargetChange('targeted')}
                className={`cursor-pointer py-2.5 px-3 rounded-md ${targetValue === 'targeted' ? 'bg-amber-50' : ''}`}
              >
                <div className="flex items-center w-full">
                  <Target className={`mr-3 h-4 w-4 ${targetValue === 'targeted' ? 'text-amber-600' : 'text-slate-400'}`} />
                  <span className={targetValue === 'targeted' ? 'font-medium text-amber-700' : 'text-slate-700'}>Hedefli</span>
                </div>
              </DropdownMenuItem>

              <DropdownMenuItem
                onClick={() => handleTargetChange('global')}
                className={`cursor-pointer py-2.5 px-3 rounded-md ${targetValue === 'global' ? 'bg-cyan-50' : ''}`}
              >
                <div className="flex items-center w-full">
                  <Building2 className={`mr-3 h-4 w-4 ${targetValue === 'global' ? 'text-cyan-600' : 'text-slate-400'}`} />
                  <span className={targetValue === 'global' ? 'font-medium text-cyan-700' : 'text-slate-700'}>Genel (Tüm Firmalar)</span>
                </div>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          {onAddBanner && (
            <Button
              onClick={onAddBanner}
              className="h-11 px-5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white shadow-sm"
            >
              <Plus className="mr-2 h-4 w-4" />
              Yeni Duyuru Ekle
            </Button>
          )}
        </div>

        {/* Aktif filtre belirteçleri */}
        {isAnyFilterActive && (
          <div className="flex flex-wrap gap-2 p-3 bg-slate-50 rounded-lg border border-slate-100">
            <span className="text-xs font-medium text-slate-600 flex items-center">
              Aktif Filtreler:
            </span>

            {statusValue === 'active' && (
              <Badge className="flex items-center gap-1.5 px-3 py-1 bg-green-100 text-green-800 border-0 hover:bg-green-200">
                <CheckCircle className="h-3 w-3" />
                Aktif
                <X
                  className="h-3.5 w-3.5 ml-1 cursor-pointer hover:text-green-600"
                  onClick={() => onStatusChange('all')}
                />
              </Badge>
            )}

            {statusValue === 'scheduled' && (
              <Badge className="flex items-center gap-1.5 px-3 py-1 bg-blue-100 text-blue-800 border-0 hover:bg-blue-200">
                <Clock className="h-3 w-3" />
                Planlanmış
                <X
                  className="h-3.5 w-3.5 ml-1 cursor-pointer hover:text-blue-600"
                  onClick={() => onStatusChange('all')}
                />
              </Badge>
            )}

            {statusValue === 'expired' && (
              <Badge className="flex items-center gap-1.5 px-3 py-1 bg-red-100 text-red-800 border-0 hover:bg-red-200">
                <XCircle className="h-3 w-3" />
                Süresi Dolmuş
                <X
                  className="h-3.5 w-3.5 ml-1 cursor-pointer hover:text-red-600"
                  onClick={() => onStatusChange('all')}
                />
              </Badge>
            )}

            {statusValue === 'inactive' && (
              <Badge className="flex items-center gap-1.5 px-3 py-1 bg-gray-100 text-gray-800 border-0 hover:bg-gray-200">
                <CircleSlash className="h-3 w-3" />
                Pasif
                <X
                  className="h-3.5 w-3.5 ml-1 cursor-pointer hover:text-gray-600"
                  onClick={() => onStatusChange('all')}
                />
              </Badge>
            )}

            {displayTypeValue === 'banner' && (
              <Badge className="flex items-center gap-1.5 px-3 py-1 bg-purple-100 text-purple-800 border-0 hover:bg-purple-200">
                <Monitor className="h-3 w-3" />
                Banner
                <X
                  className="h-3.5 w-3.5 ml-1 cursor-pointer hover:text-purple-600"
                  onClick={() => onDisplayTypeChange?.('all')}
                />
              </Badge>
            )}

            {displayTypeValue === 'news' && (
              <Badge className="flex items-center gap-1.5 px-3 py-1 bg-blue-100 text-blue-800 border-0 hover:bg-blue-200">
                <Newspaper className="h-3 w-3" />
                Haber
                <X
                  className="h-3.5 w-3.5 ml-1 cursor-pointer hover:text-blue-600"
                  onClick={() => onDisplayTypeChange?.('all')}
                />
              </Badge>
            )}

            {displayTypeValue === 'modal' && (
              <Badge className="flex items-center gap-1.5 px-3 py-1 bg-orange-100 text-orange-800 border-0 hover:bg-orange-200">
                <Maximize2 className="h-3 w-3" />
                Modal
                <X
                  className="h-3.5 w-3.5 ml-1 cursor-pointer hover:text-orange-600"
                  onClick={() => onDisplayTypeChange?.('all')}
                />
              </Badge>
            )}

            {targetValue === 'targeted' && (
              <Badge className="flex items-center gap-1.5 px-3 py-1 bg-amber-100 text-amber-800 border-0 hover:bg-amber-200">
                <Target className="h-3 w-3" />
                Hedefli
                <X
                  className="h-3.5 w-3.5 ml-1 cursor-pointer hover:text-amber-600"
                  onClick={() => onTargetChange('all')}
                />
              </Badge>
            )}

            {targetValue === 'global' && (
              <Badge className="flex items-center gap-1.5 px-3 py-1 bg-cyan-100 text-cyan-800 border-0 hover:bg-cyan-200">
                <Building2 className="h-3 w-3" />
                Genel
                <X
                  className="h-3.5 w-3.5 ml-1 cursor-pointer hover:text-cyan-600"
                  onClick={() => onTargetChange('all')}
                />
              </Badge>
            )}

            <Button
              variant="ghost"
              size="sm"
              onClick={resetFilters}
              className="h-7 px-3 text-xs text-slate-600 hover:text-slate-800 hover:bg-slate-200"
            >
              Tümünü Temizle
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
